import { Button, Card, Select, Space, message, Alert } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api';
import { getScheduleTimezone, setScheduleTimezone, SCHEDULE_TIMEZONE_OPTIONS } from '../../utils/scheduleTime';

const STORAGE_KEY_GOOGLE_CLIENT_ID = 'google_calendar_client_id';

const ScheduleSettings = () => {
  const navigate = useNavigate();
  const [googleStatus, setGoogleStatus] = useState({ connected: false, calendar_id: null, client_id: null });
  const [loading, setLoading] = useState(false);
  const [clientIdChanged, setClientIdChanged] = useState(false);
  const [displayTz, setDisplayTz] = useState(() => getScheduleTimezone());

  const fetchGoogleStatus = async () => {
    try {
      const res = await axios.get('/api/schedules/google-calendar/status/');
      const currentClientId = res.data.client_id || '';
      const storedClientId = localStorage.getItem(STORAGE_KEY_GOOGLE_CLIENT_ID);
      
      setGoogleStatus({ 
        connected: res.data.connected, 
        calendar_id: res.data.calendar_id,
        client_id: currentClientId,
      });

      // 检测环境变量是否改变
      if (res.data.connected && storedClientId && storedClientId !== currentClientId && currentClientId) {
        setClientIdChanged(true);
        message.warning('检测到 Google Client ID 已更改，请重新授权以确保功能正常', 5);
      } else if (!storedClientId && currentClientId) {
        // 首次连接，保存当前的 client_id
        localStorage.setItem(STORAGE_KEY_GOOGLE_CLIENT_ID, currentClientId);
      } else if (res.data.connected && currentClientId) {
        // 更新存储的 client_id（如果已连接）
        localStorage.setItem(STORAGE_KEY_GOOGLE_CLIENT_ID, currentClientId);
      }
    } catch {
      setGoogleStatus({ connected: false, calendar_id: null, client_id: null });
    }
  };

  useEffect(() => {
    fetchGoogleStatus();
  }, []);

  const handleConnectGoogle = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/schedules/google-calendar/connect/');
      if (res.data?.url) {
        // 保存当前的 client_id 到 localStorage
        const currentClientId = googleStatus.client_id;
        if (currentClientId) {
          localStorage.setItem(STORAGE_KEY_GOOGLE_CLIENT_ID, currentClientId);
        }
        setClientIdChanged(false);
        window.location.href = res.data.url;
      }
    } catch {
      setLoading(false);
    }
  };

  const handleReconnectGoogle = async () => {
    // 清除旧的 client_id 记录，强制重新授权
    localStorage.removeItem(STORAGE_KEY_GOOGLE_CLIENT_ID);
    setClientIdChanged(false);
    await handleConnectGoogle();
  };

  return (
    <div style={{ padding: 24, background: '#fffbe9', minHeight: '100%', overflow: 'auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Button onClick={() => navigate('/schedules')}>返回排课</Button>
      </div>
      <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 640 }}>
        <Card title="邮件设置">
          <p style={{ color: '#666', marginBottom: 8 }}>用于排课提醒、系统通知等邮件发送。请在 <strong>后端环境变量</strong>（如 backend/.env）中配置 SMTP：</p>
          <ul style={{ color: '#666', marginBottom: 8, paddingLeft: 20 }}>
            <li><code>EMAIL_BACKEND</code>：默认 <code>django.core.mail.backends.smtp.EmailBackend</code></li>
            <li><code>EMAIL_HOST</code>：SMTP 服务器地址（如 smtp.qq.com、smtp.163.com）</li>
            <li><code>EMAIL_PORT</code>：端口，通常 587（TLS）或 465（SSL）</li>
            <li><code>EMAIL_USE_TLS</code>：是否使用 TLS，一般为 true</li>
            <li><code>EMAIL_HOST_USER</code> / <code>EMAIL_HOST_PASSWORD</code>：发件账号与密码或授权码</li>
            <li><code>DEFAULT_FROM_EMAIL</code>：发件人显示邮箱</li>
          </ul>
          <p style={{ color: '#999', fontSize: 12 }}>未配置 EMAIL_HOST 时，排课提醒等将不会发送邮件。详见项目根目录 env.example 中「邮件（通知/提醒）」一节。</p>
        </Card>
        <Card title="企微授权设置">
          <p style={{ color: '#666' }}>企业微信通知配置入口。请在环境变量中配置 WECOM_CORP_ID、WECOM_AGENT_SECRET 等，此处仅作说明与占位。</p>
          <Button disabled>已配置 / 去配置</Button>
        </Card>
        <Card title="WhatsApp 授权设置">
          <p style={{ color: '#666' }}>WhatsApp 通知配置入口。请在环境变量中配置 WHATSAPP_TOKEN 等，此处仅作说明与占位。</p>
          <Button disabled>已配置 / 去配置</Button>
        </Card>
        <Card title="显示时区">
          <p style={{ color: '#666', marginBottom: 8 }}>排课列表、日历与详情中的时间将按此时区显示；不选则使用北京时间。</p>
          <Select
            placeholder="选择时区（不选即北京时间）"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 280 }}
            value={displayTz || undefined}
            onChange={(v) => {
              setScheduleTimezone(v || '');
              setDisplayTz(v || getScheduleTimezone());
              message.success('已切换显示时区，排课页时间将按新时区显示');
            }}
            options={SCHEDULE_TIMEZONE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
        </Card>
        <Card title="Google 日历同步">
          <p style={{ color: '#666' }}>连接后，排课将同步到 Google 日历。</p>
          {clientIdChanged && (
            <Alert
              message="环境变量已更改"
              description="检测到 Google Client ID 已更改，请重新授权以确保功能正常。"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              action={
                <Button size="small" type="primary" onClick={handleReconnectGoogle} loading={loading}>
                  重新授权
                </Button>
              }
            />
          )}
          {googleStatus.connected ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <span style={{ color: 'green' }}>已连接</span>
                {googleStatus.calendar_id && <span>日历 ID: {googleStatus.calendar_id}</span>}
              </Space>
              <Button type="default" loading={loading} onClick={handleReconnectGoogle}>
                重新授权
              </Button>
            </Space>
          ) : (
            <Button type="primary" loading={loading} onClick={handleConnectGoogle}>
              连接 Google 账号
            </Button>
          )}
        </Card>
      </Space>
    </div>
  );
};

export default ScheduleSettings;
